<!-- Form for picking a predefined price or inputting your own -->
<template>
	<div>
		<div
			class="grid-auto-rows-auto grid w-full grid-cols-1"
			v-if="product && activePlan"
		>
			<div
				v-for="option in product.pricingOptions"
				:key="option.id"
				class="my-2 flex cursor-pointer rounded-lg border-2 p-4 pt-6 shadow-lg"
				:class="
					isLineItemInPlan(product, activePlan, option.price)
						? 'border-accent-500'
						: 'border-transparent'
				"
				data-test-id="price-option-card"
				@click="clickPriceOption(option.price)"
			>
				<div class="flex-1">
					<div class="flex">
						<div class="font-bold text-slate-500">{{ option.name }}</div>
						<!-- Recommended -->
						<div
							v-if="product.refSpbPricingRecommended === option.id"
							class="text-accent-500 ml-4 flex items-center text-xs font-bold tracking-wider uppercase"
						>
							<StarIcon class="mr-1 h-5 w-5" />
							{{ t('priceOptions.recommended') }}
						</div>
					</div>
					<div class="text-secondary-900 text-[44px] font-bold">
						{{ formatPrice(option.price) }}
					</div>
				</div>
				<PlanAvatar
					class="mt-5"
					:plan="activePlan"
					:index="activePlanIndex"
					:filled="isLineItemInPlan(product, activePlan, option.price)"
				/>
			</div>
			<!-- Custom User Input Price -->
			<div
				class="overflow-hidden transition-all duration-300 ease-in-out"
				:class="showOtherPriceOption ? 'max-h-40' : 'max-h-0'"
			>
				<div
					class="my-2 flex cursor-pointer rounded-lg border-2 p-4 pt-6 shadow-lg"
					:class="isOtherSelected ? 'border-accent-500' : 'border-transparent'"
					@click="clickCustomPriceOption()"
					data-test-id="user-input-price-option-card"
				>
					<div class="flex-1">
						<div class="font-bold text-slate-500">
							{{
								product.pricingOptions.length === 0
									? t('priceOptions.custom')
									: t('priceOptions.other')
							}}
						</div>
						<FormKit
							type="form"
							:actions="false"
							@submit.prevent
							:key="activePlanIndex"
						>
							<FormKit
								id="other-price-input"
								type="number"
								name="otherPrice"
								outer-class="my-3 rounded-lg"
								v-model="otherPrice"
								@click.stop
								@focus="otherPrice = otherPrice === '0' ? '' : otherPrice"
								step="1"
							>
								<template #prefix>
									<span class="text-secondary-400 mr-2">$</span>
								</template>
							</FormKit>
						</FormKit>
					</div>
					<PlanAvatar
						class="mt-5 ml-5"
						:plan="activePlan"
						:index="activePlanIndex"
						:filled="isOtherSelected"
					/>
				</div>
			</div>
			<!-- Show/Hide Custom Input -->
			<div
				class="my-3 flex w-fit cursor-pointer items-center"
				@click="showHideCustomInput"
				v-if="product.pricingOptions.length > 0"
			>
				<component
					:is="showOtherPriceOption ? MinusCircleIcon : PlusCircleIcon"
					class="text-secondary-300 h-6 w-6"
				/>
				<span class="text-secondary-400 ml-2 font-bold tracking-tight">{{
					showOtherPriceOption ? t('merchandise.less') : t('merchandise.more')
				}}</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import PlanAvatar from '../presentation/PlanAvatar.vue'
import { type PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useSessionStore } from '@/stores/session'
import { FormKit } from '@formkit/vue'
import {
	StarIcon,
	PlusCircleIcon,
	MinusCircleIcon,
} from '@heroicons/vue/24/solid'
import { useDebounceFn } from '@vueuse/core'
import { computed, onMounted, ref, watch, type PropType } from 'vue'

const { t } = usePresentationLocale()

const {
	formatPrice,
	isLineItemInPlan,
	findLineItemInPlan,
	findLineItemInPlanIncludingSoftDeleted,
	findParentPackageForSubItem,
} = useMerchandise()

const props = defineProps({
	product: Object as PropType<PbLineItemProductAndPricingOptions>,
	activePlanIndex: { type: Number, required: true },
})
const sessionStore = useSessionStore()

const activePlan = computed(() => {
	return sessionStore.activePlans?.[props.activePlanIndex]
})

const showOtherPriceOption = ref<boolean>(false)
const otherPrice = ref<string>('0')

// Check if the other price is selected
const isOtherSelected = computed(() => {
	if (!props.product || !activePlan.value) return false

	// Check if this product is a subitem inside a package
	const parent = findParentPackageForSubItem(activePlan.value, props.product.id)

	if (!parent) {
		// Look for this product in the cart directly
		const existing = findLineItemInPlan(props.product, activePlan.value)

		// "Other" is considered selected if the product exists in the cart and its price does not match any of the predefined pricing options
		return (
			!!existing &&
			!props.product.pricingOptions.some((p) => p.price === existing.price)
		)
	}

	// if this is a subitem, check the current price of the subitem
	const subPrice = parent.subItem.price ?? 0

	return !props.product.pricingOptions.some((p) => p.price === subPrice)
})

/**
 * Add the custom item to the cart at the specified price
 * Restore it if it already exists and is currently soft deleted
 * Remove it if it already exists and is not soft deleted
 * Add it if it doesn't exist
 **/
function clickCustomPriceOption() {
	if (!props.product || !activePlan.value) return

	const price = parseFloat(otherPrice.value)
	const normalizedPrice = Number.isFinite(price) ? price : 0

	if (normalizedPrice === 0) {
		focusOtherPriceInput()
		return
	}

	const existing = findLineItemInPlanIncludingSoftDeleted(
		props.product,
		activePlan.value
	)

	if (!existing) {
		activePlan.value.addCartProduct(props.product.id, 1, normalizedPrice)
		focusOtherPriceInput()
		return
	}

	if (existing.isSoftDeleted) {
		existing.isSoftDeleted = false
		existing.price = normalizedPrice
		focusOtherPriceInput()
	} else {
		activePlan.value.delete(existing)
	}
}

function focusOtherPriceInput() {
	document.getElementById('other-price-input')?.focus()
}

// On mount, if the product has no pricing options, show the custom input
onMounted(() => {
	if (!props.product || !activePlan.value) return

	if (props.product.pricingOptions.length === 0) {
		showOtherPriceOption.value = true
	}

	const parent = findParentPackageForSubItem(activePlan.value, props.product.id)
	const subItem = parent?.subItem
	const existing = findLineItemInPlanIncludingSoftDeleted(
		props.product,
		activePlan.value
	)

	if ((subItem && !subItem.isSoftDeleted) || existing) {
		showOtherPriceOption.value = true
		otherPrice.value = String(subItem?.price ?? existing?.price ?? 0)
	}
})

/**
 * Watch the otherPrice and update the cart item if it's selected and exists.
 * This is called when the user types in the custom price input.
 */
const ignoreNextSet = ref(false)
watch(
	otherPrice,
	useDebounceFn((newPrice) => {
		if (!props.product || !activePlan.value) {
			return
		}

		if (ignoreNextSet.value) {
			ignoreNextSet.value = false
			return
		}

		const parsedPrice = Number.parseFloat(newPrice)
		const normalizedPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0
		const existingCartItem = findLineItemInPlanIncludingSoftDeleted(
			props.product,
			activePlan.value
		)

		if (existingCartItem) {
			if (normalizedPrice === 0) {
				activePlan.value?.delete(existingCartItem)

				if (newPrice === '') {
					ignoreNextSet.value = true
					otherPrice.value = (existingCartItem.price ?? 0).toString()
					existingCartItem.price = normalizedPrice
				}
			} else {
				existingCartItem.price = normalizedPrice
				existingCartItem.isSoftDeleted = false
			}
		} else if (normalizedPrice != 0) {
			activePlan.value.addCartProduct(props.product.id, 1, normalizedPrice)
		}
	}, 500)
)

// Watch for changes in the subitem price and update the otherPrice if needed
watch(
	() =>
		props.product && activePlan.value
			? findParentPackageForSubItem(activePlan.value, props.product.id)?.subItem
					?.price
			: undefined,
	(newPrice) => {
		if (newPrice != null) otherPrice.value = String(newPrice)
	}
)

/**
 * Add the item to the cart at the specified price
 * Restore it if it already exists and is currently soft deleted
 * Remove it if it already exists and is not soft deleted
 * Add it if it doesn't exist
 **/
function clickPriceOption(price: number) {
	if (!props.product || !activePlan.value) return

	const existing = findLineItemInPlanIncludingSoftDeleted(
		props.product,
		activePlan.value
	)

	if (existing) {
		if (existing.isSoftDeleted) {
			existing.isSoftDeleted = false
			existing.price = price
		} else {
			activePlan.value.delete(existing)
		}
	} else {
		activePlan.value.addCartProduct(props.product.id, 1, price)
	}
}

function showHideCustomInput() {
	showOtherPriceOption.value = !showOtherPriceOption.value

	// When collapse, if other price is selected then remove selection.
	if (!showOtherPriceOption.value && isOtherSelected.value) {
		if (props.product === undefined) throw new Error('Product is undefined')
		if (!activePlan.value) throw new Error('Plan is undefined')

		const existingCartItem = findLineItemInPlan(props.product, activePlan.value)

		if (existingCartItem) {
			activePlan.value.delete(existingCartItem)
		}
	}
}
</script>
